import { computedFrom, observable } from "aurelia-binding";
import { autoinject } from "aurelia-dependency-injection";
import { RiskScoreHelper, VetClientModel, VetRuleOutcomeStepEnum, type VetRuleRunClientModel } from "../../../../../../models/vetting/vetting.models";
import { VettingService } from "../../../../../../services/vetting/vetting.service";
import * as Enumerable from "linq";
import { SignalClientService } from "@rightship-tech/common-ui/services/basic/signal-client.service";
import { SignalFunctions } from "../../../../../../services/basics/signal-functions";
import { useView } from "aurelia-framework";

@autoinject
@useView('views/pages/modules/vetting/steps/rules-run/vet-rules-run.html')
export class VetRulesRun {

    runs: VetRuleRunClientModel[] = [];
    filter: string;
    public runsLoading: boolean = true;
    public riskScoreSeperatorShowing: boolean;

    @observable
    selectedRun: VetRuleRunClientModel;

    isPass: boolean;
    isFail: boolean;
    isNa: boolean;
    isChanged: boolean = true;
    vetTasksUpdated: (params: { vetId: number }) => void = (params: { vetId: number }) => {
        if (this.vetId == params.vetId) this.reload();
    };


    filterRulePass() { this.isPass = !this.isPass; }
    filterRuleFail() { this.isFail = !this.isFail; }
    filterRuleNa() { this.isNa = !this.isNa; }
    filterRuleChanged() { this.isChanged = !this.isChanged; }

    constructor(private vettingService: VettingService, private signalClientService: SignalClientService) { }

    vetId: number;
    async activate(item: VetClientModel) {
        this.vetId = item.vetId;
        this.signalClientService.subscribe(SignalFunctions.vetTaskUpdateChanged, this.vetTasksUpdated);
        await this.reload();
    }

    detached() {
        this.signalClientService.unsubscribe(SignalFunctions.vetTaskUpdateChanged, this.vetTasksUpdated);
    }

    async reload() {
        this.runsLoading = true;
        this.runs = await this.vettingService.getVetRuleRunsClientModel(this.vetId);
        if (this.runs.length > 0) {
            this.selectRun(this.selectedRun ? this.selectedRun : this.runs[0]);
        }
        this.runsLoading = false;
    }

    toggleSectionExpander(head: HTMLElement, body: HTMLElement) {
        $(body).slideToggle();
        if ($(head).hasClass('is-open')) {
            $(head).removeClass('is-open');
        } else {
            $(head).addClass('is-open');
        }
    }

    async selectRun(run: VetRuleRunClientModel) {
        this.selectedRun = run;
        if (!run.items || run.items.length == 0) {
            run.items = await this.vettingService.getVetRuleRunItemsClientModel(run.vettingRuleRunId, null);
        }
        if (!run.risks || run.risks.length == 0) {
            run.risks = await this.vettingService.getVetRuleRunItemRiskClientModel(run.vettingRuleRunId);

            for(const score of run.risks) {
                score.riskScore =  RiskScoreHelper.calculateRiskScore({
                    countAllHigh: score.countHighFail + score.countHighPass,
                    countAllLow:score.countLowFail + score.countLowPass,
                    countAllMedium: score.countMediumFail + score.countMediumPass,
                    countHighFail: score.countHighFail,
                    countLowFail: score.countLowFail,
                    countMediumFail: score.countMediumFail,
                    countOraFail: score.countOraFail
                });
            }
        }
    }

    @computedFrom("selectedRun", "selectedRun.items", "selectedRun.risks", "filter", "isPass", "isFail", "isNa", "isChanged")
    get filteredRules() {
        if (this.selectedRun && this.selectedRun.items) {
            let items = this.selectedRun.items;
            if (this.filter)
                items = items.filter(rule => ((rule.sequenceNumber ? rule.sequenceNumber : rule.ruleCode) + ' - ' + rule.title).toLowerCase().indexOf(this.filter.toLowerCase()) > -1);

            return items.filter(x => {
                let include = !this.isFail && !this.isPass && !this.isNa && !this.isChanged;

                if (this.isFail)
                    include = include || x.vetTaskOutcome == VetRuleOutcomeStepEnum.Fail;
                if (this.isPass)
                    include = include || x.vetTaskOutcome == VetRuleOutcomeStepEnum.Pass;
                if (this.isNa)
                    include = include || x.vetTaskOutcome == VetRuleOutcomeStepEnum.NA;
                if (this.isChanged)
                    include = include || x.vetTaskOutcome != x.previousVetTaskOutcome;

                return include;
            });
        }

        return [];
    }

    @computedFrom("selectedRun")
    get vetRiskScoresEnumerable() { return Enumerable.from(this.selectedRun.risks).where(x => x.riskCategory == 'All'); }

    @computedFrom("selectedRun")
    get countLowFail() { return this.vetRiskScoresEnumerable.sum(x => x.countLowFail); }

    @computedFrom("selectedRun")
    get countMediumFail() { return this.vetRiskScoresEnumerable.sum(x => x.countMediumFail); }

    @computedFrom("selectedRun")
    get countHighFail() { return this.vetRiskScoresEnumerable.sum(x => x.countHighFail); }

    @computedFrom("selectedRun")
    get countOraFail() { return this.vetRiskScoresEnumerable.sum(x => x.countOraFail); }

    @computedFrom("selectedRun")
    get countLowPass() { return this.vetRiskScoresEnumerable.sum(x => x.countLowPass); }

    @computedFrom("selectedRun")
    get countMediumPass() { return this.vetRiskScoresEnumerable.sum(x => x.countMediumPass); }

    @computedFrom("selectedRun")
    get countHighPass() { return this.vetRiskScoresEnumerable.sum(x => x.countHighPass); }

    @computedFrom("selectedRun")
    get countOraPass() { return this.vetRiskScoresEnumerable.sum(x => x.countOraPass); }


    @computedFrom("selectedRun")
    get countAllLow() { return this.vetRiskScoresEnumerable.sum(x => x.countLowFail + x.countLowPass); }

    @computedFrom("selectedRun")
    get countAllMedium() { return this.vetRiskScoresEnumerable.sum(x => x.countMediumFail + x.countMediumPass); }

    @computedFrom("selectedRun")
    get countAllHigh() { return this.vetRiskScoresEnumerable.sum(x => x.countHighFail + x.countHighPass); }

    @computedFrom("selectedRun")
    get countAllOra() { return this.vetRiskScoresEnumerable.sum(x => x.countOraFail + x.countOraPass); }

    clearFilter() {
        this.filter = "";
    }

    back() {
        this.selectedRun = null;
    }

    //Show seperator the first time its not "Critical" or "Soft" rule within the loop
    showSeperatorForRiskScores(risk: string) {
        if(risk == "All") {
            this.riskScoreSeperatorShowing = true;
        }
        else if(risk == "Critical") {
            this.riskScoreSeperatorShowing = true;
        }
        else if(risk == "Soft") {
            this.riskScoreSeperatorShowing = true;
        }
        else if(this.riskScoreSeperatorShowing) {
            return true;
        }
    }
}